import type { TranslationKey } from "@privacy/translations";
import { useTranslation } from "@privacy/translations";
import type { GridColDef } from "@mui/x-data-grid-premium";
import Stack from "@mui/material/Stack";
import { Navigate, useLocation } from "react-router";
import { DynamicGrid } from "../../dynamicGrid";
import { usePrivacyDocumentTitle } from "../../shared/usePrivacyDocumentTitle";
import { EmptyState } from "../../shared/EmptyState";
import { usePrivacyNavigate } from "../../shared/usePrivacyNavigate";
import { dpiaByIdQueryOptions, dpiaGridViewQueryOptions, dpiaFormQueryOptions } from "../../api/dpia";
import type { GridViewDpia } from "../../api/dpia.types";
import { requiredColDef } from "../../dynamicGrid/misc";
import { DpiaIcon2, CircleXIcon, PersonIcon3, RegistrationControllerIcon3, YesIcon } from "../../shared/icons";
import { EmptyCell } from "../../dynamicGrid/columnDefinitions";
import { HACK_alignIconWithLabelBox } from "@privacy/ui";
import { DpiaGridToolbar } from "./DpiaGridToolbar";
import { DpiaStatusChip } from "./DpiaStatusChip";
import { DpiaMultiStepForm } from "./DpiaMultiStepForm";
import { DpiaPreAssessmentForm } from "./DpiaPreAssessmentForm";
import { DpiaOverviewDrawer } from "./DpiaOverviewDrawer";
import { DpiaHistoryDrawer } from "./DpiaHistoryDrawer";
import { DpiaActionMenu } from "./DpiaDrawerHeader";
import { prefetch } from "../../api/prefetch";
import { useQuery } from "@tanstack/react-query";

const UPDATED_AT_FIELD = "updatedAt";

const DpiaGridPage = () => {
  usePrivacyDocumentTitle("Sidebar.Dpia");
  const { t, formatDate } = useTranslation();
  const { navigate, paths } = usePrivacyNavigate();
  const dpiaGridViewQuery = useQuery(dpiaGridViewQueryOptions());

  return (
    <DynamicGrid.Grid
      rowsData={dpiaGridViewQuery.data?.responses}
      form="no-form"
      getRowId={getRowId}
      mainColumnProps={{
        headerName: t("Dpia.Grid.NameHeader"),
        renderLabel: ({ name }) => name,
        renderIcon: ({ iconProps }) => <DpiaIcon2 {...iconProps} />,
      }}
      getColumns={() => [
        typeColumn(t),
        preAssessmentStatusColumn(t),
        dpiaStatusColumn(t),
        connectedRegistrationsColumn(t),
        responsibleColumn({ headerName: t("Dpia.Grid.ResponsibleHeader"), field: "responsible" }),
        DynamicGrid.dateTimeColumn({ headerName: t("Dpia.Grid.UpdatedAtHeader"), field: UPDATED_AT_FIELD, formatDate }),
      ]}
      toolbarTabs={{
        queryKey: "type",
        filter: (row, activeTab) => activeTab === "all" || row.type === activeTab,
        tabs: [
          { labelKey: "Dpia.Grid.FilterTabs.all", value: "all" },
          { labelKey: "Dpia.Grid.FilterTabs.assessments", value: "internal" },
          { labelKey: "Dpia.Grid.FilterTabs.external", value: "external" },
        ],
      }}
      defaultSort={{ field: UPDATED_AT_FIELD, sort: "desc" }}
      onRowSelect={currentId => navigate(paths.dpiaById(currentId))}
      Toolbar={DpiaGridToolbar}
      Footer={DpiaGridFooter}
      NoRowsOverlay={NoDpiaOverlay}
      texts={{ searchFieldPlaceholder: t("Dpia.Grid.SearchFieldLabel") }}
      renderActionMenu={({ row, selectedRowId }) => (
        <DpiaActionMenu
          dpiaId={row.id}
          onDeleted={() => {
            if (selectedRowId === row.id) navigate(paths.dpiaGrid());
          }}
        />
      )}
    />
  );
};

const getRowId = (row: GridViewDpia) => row.id;

const typeColumn = (t: (translation: TranslationKey) => string): GridColDef<GridViewDpia> => ({
  ...requiredColDef({ headerName: t("Dpia.Grid.TypeHeader"), field: "type" }),
  width: 160,
  renderCell: ({ value }) => (
    <DynamicGrid.Cell withExtraSpacingTop={false}>
      <DynamicGrid.TextCellContent value={value} />
    </DynamicGrid.Cell>
  ),
  valueGetter: (_, row) => t(`Dpia.Type.${row.type}`),
});

const connectedRegistrationsColumn = (t: (translation: TranslationKey) => string): GridColDef<GridViewDpia> => ({
  ...requiredColDef({ headerName: t("Dpia.Grid.ConnectedRegistrationsHeader"), field: "connectedRegistrationsCount" }),
  description: t("Dpia.Grid.ConnectedRegistrationsDescription"),
  renderCell: ({ value }) => (
    <DynamicGrid.Cell withExtraSpacingTop={false}>
      <Stack direction="row" sx={{ alignItems: "center", gap: 1, fontSize: theme => theme.typography.pxToRem(14) }}>
        <RegistrationControllerIcon3 size={16} />
        <HACK_alignIconWithLabelBox>{value}</HACK_alignIconWithLabelBox>
      </Stack>
    </DynamicGrid.Cell>
  ),
});

const preAssessmentStatusColumn = (t: (translation: TranslationKey) => string): GridColDef<GridViewDpia> => ({
  ...requiredColDef({ headerName: t("DpiaPreAssessment.PreAssessment"), field: "dpiaRecommended" }),
  width: 250,
  renderCell: ({ row }) =>
    row.type === "external" ? (
      <EmptyCell />
    ) : (
      <DynamicGrid.Cell>
        <PreAssessmentStatusCell
          dpiaRecommended={row.dpiaRecommended}
          preAssessmentIsSkipped={row.dpiaRecommended === undefined}
          t={t}
        />
      </DynamicGrid.Cell>
    ),
});

type PreAssessmentStatusCellProps = {
  dpiaRecommended?: boolean;
  preAssessmentIsSkipped?: boolean;
  t: (translation: TranslationKey) => string;
};

const PreAssessmentStatusCell = ({ dpiaRecommended, preAssessmentIsSkipped, t }: PreAssessmentStatusCellProps) => {
  if (dpiaRecommended === undefined || preAssessmentIsSkipped) {
    return <EmptyCell />;
  }

  return (
    <DynamicGrid.Cell withExtraSpacingTop={false}>
      <DynamicGrid.TextCellContent
        value={dpiaRecommended ? t("Dpia.Grid.DpiaRecommended") : t("Dpia.Grid.DpiaNotRequired")}
        Icon={dpiaRecommended ? RecommendedIcon : CircleXIcon}
      />
    </DynamicGrid.Cell>
  );
};

const RecommendedIcon = () => <YesIcon color={theme => theme.palette.primary.main} />;

const dpiaStatusColumn = (t: (translation: TranslationKey) => string): GridColDef<GridViewDpia> => ({
  ...requiredColDef({ headerName: t("Dpia.Grid.StatusHeader"), field: "status" }),
  width: 250,
  renderCell: ({ row }) => {
    if (row.status === "preAssessment") return <EmptyCell />;

    return (
      <DynamicGrid.Cell>
        <DpiaStatusChip status={row.status} />
      </DynamicGrid.Cell>
    );
  },
  valueGetter: (_, row) => t(`DpiaStatus.${row.status}`),
});

const responsibleColumn = ({ headerName }: { headerName: string; field: string }): GridColDef<GridViewDpia> => ({
  ...requiredColDef({ headerName, field: "responsible" }),
  width: 260,
  renderCell: ({ row }) => (row.responsible ? <ResponsibleCell responsible={row.responsible} /> : null),
  valueGetter: (_, row) => (row.responsible ? `${row.responsible.firstName} ${row.responsible.lastName}` : null),
});

const ResponsibleCell = ({ responsible }: { responsible: GridViewDpia["responsible"] }) => {
  return (
    <DynamicGrid.Cell withExtraSpacingTop={false}>
      <DynamicGrid.TextCellContent value={`${responsible.firstName} ${responsible.lastName}`} Icon={PersonIcon3} />
    </DynamicGrid.Cell>
  );
};

const DpiaDrawerFallbackRedirect = () => {
  const dpiaId = DynamicGrid.useSelectedRowId();
  const { paths } = usePrivacyNavigate();
  const { search } = useLocation();
  return <Navigate to={`${paths.dpiaById(dpiaId)}${search}`} replace />;
};

const DpiaGridFooter = () => <DynamicGrid.Footer numberOfRowsTextKey="Dpia.Grid.NumberOfRows" />;

const NoDpiaOverlay = () => {
  return <EmptyState labelKey="Dpia.Grid.NoRows" />;
};

export const dpiaGridPageRoutes = DynamicGrid.setupRoutes({
  gridElement: <DpiaGridPage />,
  rowDrawerDefaultPrefetcher: ({ rowId }) => {
    void prefetch(dpiaByIdQueryOptions(rowId));
    void prefetch(dpiaFormQueryOptions());
  },
  gridPrefetcher: () => prefetchDpiaGridViewAndForm(),
  rowDrawerRoutes: [
    { index: true, element: <DpiaOverviewDrawer /> },
    { path: "pre-assessment/*", element: <DpiaPreAssessmentForm /> },
    { path: "form/*", element: <DpiaMultiStepForm /> },
    { path: "history/*", element: <DpiaHistoryDrawer /> },
    { path: "*", element: <DpiaDrawerFallbackRedirect /> },
  ],
});

export const prefetchDpiaGridViewAndForm = () => {
  prefetch(dpiaGridViewQueryOptions());
  prefetch(dpiaFormQueryOptions());
};
